/**
 * WelcomeModal — first-login orientation.
 *
 * Opens automatically when the current user has welcomedAt=null.
 * On dismiss, calls auth.markWelcomed() which optimistically stamps
 * welcomedAt in the store, immediately driving open to false.
 *
 * Designed to live anywhere in the tree (currently mounted on Overview);
 * it self-gates on the auth store so callers don't manage open state.
 */
import { Link } from "react-router-dom";
import { KeyRound, Bot, HelpCircle, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/stores/auth";

export function WelcomeModal() {
  const user = useAuth((s) => s.user);
  const markWelcomed = useAuth((s) => s.markWelcomed);

  // markWelcomed is optimistic: it synchronously stamps user.welcomedAt in
  // the store, so shouldOpen becomes false in the same render cycle and the
  // Dialog animates closed without any intermediate local state.
  const shouldOpen = !!user && !user.welcomedAt;

  function handleClose(): void {
    void markWelcomed();
  }

  return (
    <Dialog open={shouldOpen} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="max-w-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-aurora-soft text-violet">
            <Sparkles size={18} />
          </div>
          <div>
            <DialogTitle>Welcome to Opus, {user?.fullName?.split(" ")[0] ?? "operator"}.</DialogTitle>
            <DialogDescription>
              A 30-second tour before you dive in.
            </DialogDescription>
          </div>
        </div>

        <ul className="flex flex-col gap-3 text-sm">
          <li className="flex items-start gap-3 rounded-xl border border-line bg-surface-2/50 p-3">
            <KeyRound size={16} className="mt-0.5 shrink-0 text-violet" />
            <div className="flex-1">
              <div className="font-medium text-fg">Add your vendor keys</div>
              <p className="text-fg-dim">
                Opus calls every vendor with your credentials, not a shared one.
                Add a Meraki key on{" "}
                <Link to="/meraki/keys" className="text-violet underline">
                  Meraki → Keys
                </Link>{" "}
                and an Aruba bundle on{" "}
                <Link to="/aruba/keys" className="text-violet underline">
                  Aruba → Keys
                </Link>{" "}
                so the vendor pages light up.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3 rounded-xl border border-line bg-surface-2/50 p-3">
            <Bot size={16} className="mt-0.5 shrink-0 text-violet" />
            <div className="flex-1">
              <div className="font-medium text-fg">Try the AI agent</div>
              <p className="text-fg-dim">
                Ask it to look up devices, summarize compliance, or stage a
                change. Writes always come back as a proposal you confirm —
                nothing mutates without an explicit click.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3 rounded-xl border border-line bg-surface-2/50 p-3">
            <HelpCircle size={16} className="mt-0.5 shrink-0 text-violet" />
            <div className="flex-1">
              <div className="font-medium text-fg">Need help?</div>
              <p className="text-fg-dim">
                The{" "}
                <Link to="/help" className="text-violet underline">
                  Help page
                </Link>{" "}
                covers the basics. For access changes, key resets, or anything
                that looks broken, ping the NetEng team.
              </p>
            </div>
          </li>
        </ul>

        <div className="mt-5 flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={handleClose}>
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
